import React from 'react';
import { Link } from 'react-router-dom';
import WhiteLogo from '@/assets/icons/white_logo_lg.png';

function TermsPage() {
  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: '"Inter", "SF Pro Display", system-ui, sans-serif' }}>
      <header className="border-b border-border px-6 py-5">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 transition-opacity hover:opacity-80">
            <img src={WhiteLogo} alt="Staty" className="h-7 w-auto" />
          </Link>
          <Link
            to="/sign-up"
            className="text-sm font-medium text-muted-foreground underline-offset-2 hover:underline"
          >
            ← Back to sign up
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12">
        <h1 className="mb-2 text-3xl font-semibold tracking-tight text-foreground">Terms of Service</h1>
        <p className="mb-10 text-sm text-muted-foreground">Effective date: March 20, 2026</p>

        <div className="space-y-8 text-foreground">
          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">1. Acceptance of Terms</h2>
            <p>
              By accessing or using Staty ("the Service"), you agree to be bound by these Terms of Service. If you
              do not agree to these terms, please do not use the Service. These terms apply to all users of the
              platform, including league organizers, team managers, and other administrators.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">2. Description of Service</h2>
            <p>
              Staty is a sports league and tournament management platform that enables organizers to create and
              manage leagues, tournaments, divisions, teams, players, game schedules, statistics, and
              registrations. The platform is designed for recreational and competitive sports organizations.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">3. Account Registration & Security</h2>
            <p className="mb-3">
              To use the Service, you must create an account. Account authentication is handled by Clerk, a
              third-party identity provider. You are responsible for:
            </p>
            <ul className="list-disc space-y-1.5 pl-5">
              <li>Providing accurate and complete registration information</li>
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activity that occurs under your account</li>
              <li>Notifying us immediately of any unauthorized use of your account</li>
            </ul>
            <p className="mt-3">
              You must be at least 18 years old to create an account and use the Service as an organizer.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">4. Organizer Responsibilities</h2>
            <p className="mb-3">
              As a league or tournament organizer using Staty, you are responsible for:
            </p>
            <ul className="list-disc space-y-1.5 pl-5">
              <li>The accuracy of all league, team, player, and game data you enter into the platform</li>
              <li>Obtaining appropriate consent from players and participants before entering their personal information</li>
              <li>Complying with applicable laws and regulations in your jurisdiction</li>
              <li>Managing registration fees, waivers, and communications with your participants</li>
              <li>Ensuring your use of the platform does not violate the rights of any third party</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">5. Payment & Fees</h2>
            <p className="mb-3">
              Staty uses Stripe to process payments. By using paid features of the Service, you agree to Stripe's
              terms of service. The following applies to billing:
            </p>
            <ul className="list-disc space-y-1.5 pl-5">
              <li>Subscription fees are billed on a recurring basis as described at the time of purchase</li>
              <li>Transaction fees may apply to registration payments collected through the platform</li>
              <li>All fees are non-refundable unless otherwise stated or required by law</li>
              <li>We reserve the right to change pricing with reasonable advance notice</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">6. Prohibited Uses</h2>
            <p className="mb-3">You may not use the Service to:</p>
            <ul className="list-disc space-y-1.5 pl-5">
              <li>Violate any applicable laws or regulations</li>
              <li>Transmit spam, unsolicited communications, or malicious code</li>
              <li>Impersonate any person or entity</li>
              <li>Collect or harvest user data without authorization</li>
              <li>Interfere with or disrupt the integrity or performance of the Service</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">7. Intellectual Property</h2>
            <p>
              The Service and its original content, features, and functionality are owned by Staty and are
              protected by applicable intellectual property laws. You retain ownership of data you input into the
              platform, but grant Staty a limited license to process and display that data to provide the Service.
              You may not copy, modify, distribute, or reverse engineer any part of the Service without our prior
              written consent.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">8. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS
              OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF
              VIRUSES OR OTHER HARMFUL COMPONENTS. YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">9. Limitation of Liability</h2>
            <p>
              TO THE FULLEST EXTENT PERMITTED BY LAW, STATY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE, INCLUDING LOSS
              OF PROFITS, DATA, OR GOODWILL. OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING FROM THESE TERMS
              SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE MONTHS PRECEDING THE CLAIM.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">10. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service at any time, with or without cause, with or
              without notice. You may cancel your account at any time by contacting us. Upon termination, your
              right to use the Service ceases immediately. Provisions that by their nature should survive
              termination will remain in effect.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">11. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will provide notice of significant
              changes by updating the effective date at the top of this page or by other reasonable means.
              Continued use of the Service after changes constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">12. Governing Law</h2>
            <p>
              These Terms are governed by and construed in accordance with applicable law. Any disputes arising
              from these Terms or your use of the Service shall be resolved through binding arbitration or in
              courts of competent jurisdiction, as permitted by law.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-lg font-semibold text-foreground">13. Contact Information</h2>
            <p>
              If you have questions about these Terms of Service, please contact us at{' '}
              <a href="mailto:support@staty.app" className="font-medium text-foreground underline-offset-2 hover:underline">
                support@staty.app
              </a>.
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-border px-6 py-8">
        <div className="mx-auto max-w-3xl text-center text-sm text-muted-foreground/60">
          <p>© {new Date().getFullYear()} Staty. All rights reserved.</p>
          <div className="mt-2 flex items-center justify-center gap-4">
            <Link to="/terms" className="hover:text-muted-foreground">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-muted-foreground">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default TermsPage;
